'use client'

import type { ScenarioNodeWithVideoUrl } from '@/orval/generated/public/model'
import { ArrowDown, RotateCcw, X } from 'lucide-react'
import { Dispatch, FC, RefObject, SetStateAction } from 'react'
import InfoCTAButton from '../modal/video-info-panel/InfoCTAButton'
import VideoInfoContent from '../modal/video-info-panel/VideoInfoContent'

export type BoxInfoPanelProps = {
  currentNode: ScenarioNodeWithVideoUrl
  isTwoColumn: boolean
  /** 動画を見終わって「決める」フェーズに入っているか */
  isDecisionVisible: boolean
  /** 最初のノードに居るか（居なければ「最初から」を出す） */
  /** 現在ノードの選択肢。件数だけを見る（空なら最終ノード扱いで CV を出す） */
  choiceButtons: readonly unknown[]
  branchQuestion: string
  /** 決定エリアがパネル内に見えているか。外れていれば誘導ボタンを出す */
  isDecisionInView: boolean
  ctaUrl: string | null
  ctaLabel: string | null
  /** 本文のスクロール領域。決定エリアへの誘導で使う */
  scrollAreaRef: RefObject<HTMLDivElement | null>
  /** 決定エリア（設問＋選択肢）。可視監視と誘導の対象 */
  decisionRef: RefObject<HTMLDivElement | null>
  /** アンケートの描画先。プレイヤーが portal で流し込む */
  setSurveySlot: Dispatch<SetStateAction<HTMLDivElement | null>>
  /** 今の動画を最初から再生し直す（別の動画へは進まない・ADR-0023 A-3） */
  onReplay: () => void
  onCloseDetail: () => void
  onScrollToDecision: () => void
  onCtaClick?: () => void
  /** CVで別タブを開く直前に動画を止める */
  onPauseVideo?: () => void
}

/**
 * BOX型の右パネル（狭い画面では動画下のボトムシート）。
 * タイトル・出演者・動画テキストと、見終わったあとの操作（もう一度見る / CV）を置く。
 */
const BoxInfoPanel: FC<BoxInfoPanelProps> = ({
  currentNode,
  isTwoColumn,
  isDecisionVisible,
  choiceButtons,
  branchQuestion,
  isDecisionInView,
  ctaUrl,
  ctaLabel,
  scrollAreaRef,
  decisionRef,
  setSurveySlot,
  onReplay,
  onCloseDetail,
  onScrollToDecision,
  onCtaClick,
  onPauseVideo,
}) => (
        <div
          style={{
            position: 'relative',
            // テキスト側は読みやすい固定幅。動画個別ページのテキストカラム（620px・
            // 1行39字）と同じ行長にする。ここが可変だと余った幅ぶん本文が間延びする。
            width: isTwoColumn ? '620px' : undefined,
            flex: isTwoColumn ? '1 1 auto' : 1,
            minWidth: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: isTwoColumn ? undefined : '20px 20px 0 0',
            overflow: 'hidden',
            background: '#fff',
            color: '#172033',
          }}
          /*
           * TILE のパネルと同じクラスを当てる。見た目のためだけでなく、ダークテーマ時に
           * MarkdownView の文字色を打ち消す CSS が `.dark .info-panel .markdown-view` という
           * セレクタで書かれており、このクラスが無いと管理画面プレビュー（ダーク）で
           * 本文が白背景に白文字になって読めなくなるため。
           */
          className="info-panel"
        >
          {!isTwoColumn && (
            <button
              type="button"
              onClick={onCloseDetail}
              aria-label="詳細を閉じる"
              className="info-close-button"
              style={{ zIndex: 30 }}
            >
              <X size={20} />
            </button>
          )}
          {/* 本文の書式（padding / letter-spacing / text-align: justify）は TILE の
              .info-content をそのまま使う。個別に指定すると字間や行末揃えがズレるため。
              min-height だけはクラスに無いのでインラインで補う。 */}
          <div
            ref={scrollAreaRef}
            className={isTwoColumn ? 'info-content box-info-content' : undefined}
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              ...(isTwoColumn
                ? {}
                : {
                    padding: `22px 21px ${isDecisionVisible && choiceButtons.length === 0 && ctaUrl ? '100px' : '18px'}`,
                  }),
            }}
          >
            {/* タイトル・出演者・動画テキストは共通基盤。BOX 固有の装飾ラベルは持たない。 */}
            <VideoInfoContent currentNode={currentNode} />

            {/* 進捗表示は持たず、動画終了後は「もう一度見る」だけを残す。
                終了後にだけ出るボタンで別の動画（1本目）へ戻す形は避ける（ADR-0023 A-3）。
                以前の「最初から」は1本目へ戻していたが、別の動画へ進む形になるため今の動画の再生し直しにした。 */}
            {isDecisionVisible && (
              <button
                type="button"
                onClick={onReplay}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                  margin: `${isTwoColumn ? 24 : 16}px 0 0`,
                  padding: 0,
                  border: 0,
                  background: 'transparent',
                  color: '#808080',
                  fontFamily: 'inherit',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                }}
              >
                <RotateCcw size={12} />
                もう一度見る
              </button>
            )}

            {/* 動画終了後の操作エリア。 */}
            <div ref={decisionRef} aria-live="polite" style={{ marginTop: isTwoColumn ? '26px' : '18px' }}>
              {isDecisionVisible && choiceButtons.length === 0 && (
                <>
                  <h3
                    style={{
                      margin: 0,
                      color: '#333',
                      fontSize: isTwoColumn ? '17px' : '15px',
                      fontWeight: 800,
                      lineHeight: 1.55,
                    }}
                  >
                    {choiceButtons.length > 0 ? branchQuestion : '最後までご覧いただきありがとうございます'}
                  </h3>
                </>
              )}
            </div>
          </div>

          {/* 最終動画のCVは本文量に左右されないよう下端へ固定する。
              アンケート待機中はCSSでその一段上に積み、回答シート展開中は隠す。 */}
          {isDecisionVisible && choiceButtons.length === 0 && ctaUrl && (
            <>
              <div className="info-bottom-fade box-bottom-fade" />
              <div className="box-bottom-cta">
                <InfoCTAButton
                  ctaUrl={ctaUrl}
                  ctaLabel={ctaLabel}
                  onTrackCtaClick={onCtaClick}
                  onPauseVideo={onPauseVideo}
                />
              </div>
            </>
          )}

          {/*
           * 選択肢が画面外にあるあいだだけ出す誘導。押すとそこまでスクロールする。
           * 見えている間は出さないので、通常の視聴では一切現れない。
           * アンケートのオプトインバーもパネル下端に出るため、そちらが表示されている
           * 可能性を考えて一段上（bottom 72px）に置き、重ならないようにする。
           */}
          {isDecisionVisible && !isTwoColumn && !isDecisionInView && (
            <div
              style={{
                position: 'absolute',
                right: 0,
                bottom: '72px',
                left: 0,
                zIndex: 20,
                display: 'flex',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}
            >
              <button
                type="button"
                onClick={onScrollToDecision}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '7px',
                  padding: '10px 18px',
                  border: 0,
                  borderRadius: '999px',
                  background: 'var(--video-brand-color, var(--video-accent-color, #2196F3))',
                  color: '#fff',
                  fontFamily: 'inherit',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  pointerEvents: 'auto',
                  boxShadow:
                    '0 8px 22px -8px color-mix(in srgb, var(--video-brand-color, var(--video-accent-color, #2196F3)) 80%, transparent)',
                }}
              >
                選択肢を見る
                <ArrowDown size={15} strokeWidth={2.6} />
              </button>
            </div>
          )}

          {/* アンケートの描画先。中身（PanelSurvey）は自身が絶対配置でこのパネルを覆う。 */}
          <div ref={setSurveySlot} />
        </div>
)

export default BoxInfoPanel
